import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { PageHeader } from '@/ui/shared'
import ClaudeLogo from '@/assets/images/icons/ClaudeLogo.svg'
import ChatGPTLogo from '@/assets/images/icons/ChatGPTLogo.svg'

const MCP_DOCS_URL = 'https://docs.itsrever.com/guides/analytics-mcp'

export default function McpConnection() {
  const { t } = useTranslation()
  const serverUrl = import.meta.env.VITE_ANALYTICS_MCP_AUDIENCE ?? ''
  const clientId = import.meta.env.VITE_ANALYTICS_MCP_CLIENT_ID ?? ''

  const header = (
    <PageHeader
      title={t('mcp_connection.title')}
      description={t('mcp_connection.description')}
    />
  )

  // Both are needed to sign in: Auth0 has no dynamic client registration.
  if (!serverUrl || !clientId) {
    return (
      <>
        {header}
        <p className="px-7 py-4 text-sm text-neutral-600">
          {t('mcp_connection.unavailable')}
        </p>
      </>
    )
  }

  return (
    <>
      {header}
      <div className="flex max-w-[1000px] flex-col gap-4 px-7 py-4">
        <div className="rounded border border-solid border-neutral-200 bg-blue-50 p-4">
          <p className="text-sm leading-relaxed text-neutral-700">
            {t('mcp_connection.how_it_works')}{' '}
            <a
              href={MCP_DOCS_URL}
              target="_blank"
              rel="noreferrer"
              className="whitespace-nowrap font-medium text-shell underline underline-offset-2 hover:opacity-70"
            >
              {t('mcp_connection.docs_link')}
            </a>
          </p>
        </div>

        <section className="flex flex-col gap-3 rounded border border-neutral-200 bg-white p-4">
          <h2 className="font-roobert text-base font-semibold leading-tight text-gray-900">
            {t('mcp_connection.details_title')}
          </h2>
          <CopyField label={t('mcp_connection.server_url')} value={serverUrl} />
          <CopyField label={t('mcp_connection.client_id')} value={clientId} />
          <p className="text-xs text-neutral-500">
            {t('mcp_connection.client_secret_hint')}
          </p>
        </section>

        <section className="flex flex-col gap-3">
          <div>
            <h2 className="font-roobert text-base font-semibold leading-tight text-gray-900">
              {t('mcp_connection.setup_title')}
            </h2>
            <p className="text-xs text-gray-500">
              {t('mcp_connection.setup_description')}
            </p>
          </div>
          <ClientSetup
            logo={ClaudeLogo}
            title={t('mcp_connection.claude_title')}
            steps={t('mcp_connection.claude_steps')}
          />
          <ClientSetup
            logo={ChatGPTLogo}
            title={t('mcp_connection.chatgpt_title')}
            steps={t('mcp_connection.chatgpt_steps')}
          />
        </section>
      </div>
    </>
  )
}

function ClientSetup({
  logo,
  title,
  steps
}: {
  logo: string
  title: string
  steps: string
}) {
  return (
    <div className="flex items-start gap-3 rounded border border-neutral-200 bg-white p-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-solid border-neutral-200 bg-white">
        <img src={logo} alt="" width={22} height={22} />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-neutral-600">{steps}</p>
      </div>
    </div>
  )
}

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<number>()

  useEffect(() => () => window.clearTimeout(resetTimer.current), [])

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium">{label}</p>
      <div className="flex items-start gap-2">
        <code className="grow break-all rounded bg-neutral-100 p-2 text-xs">
          {value}
        </code>
        <Base.Button
          type="button"
          variant="secondary"
          aria-label={t('mcp_connection.copy_label', {
            label,
            interpolation: { escapeValue: false }
          })}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value)
              setCopied(true)
              // Restart the reset on every copy, so a second click keeps "Copied" up.
              window.clearTimeout(resetTimer.current)
              resetTimer.current = window.setTimeout(
                () => setCopied(false),
                1200
              )
            } catch {
              setCopied(false)
            }
          }}
        >
          {copied ? t('mcp_connection.copied') : t('mcp_connection.copy')}
        </Base.Button>
      </div>
    </div>
  )
}
